import { array, bool, func, number, oneOf, string } from 'prop-types'
import { PureComponent } from 'react'
import { testFeature } from '@opallabs/principles'
import { kuiperClassNames } from '../utils'
import { methods } from './helpers/defaults'
import { moveByReducer } from './helpers/movers'
import {
  stateFromProps,
  trackUpdateReducer,
  updateCompleteReducer,
  updateErrorReducer,
} from './helpers/updaters'
import {
  addMap,
  beingCreated,
  enqueueDelayedUpdate,
  getResponseId,
  identifierFor,
  newItemReducer,
  placeholderOptions,
  propsFor,
} from './helpers/utils'

import './styles.scss'

const MAX_REQUEST_ATTEMPTS = 10

export default class ListManager extends PureComponent {
  static propTypes = {
    addItemButton: func,
    addText: string,
    animationName: string,
    editing: bool,
    itemRenderer: func.isRequired,
    items: array,
    onItemsChanged: func,
    placeholder: oneOf(placeholderOptions),
    theme: string,
    transitionTime: number,
  }

  static defaultProps = {
    transitionTime: 500,
  }

  constructor(props) {
    super(props)

    this.state = stateFromProps(props)()
    this.renderItem = this.renderItem.bind(this)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(stateFromProps(nextProps))
  }

  updateComplete(updater, identifier, creator, item, update, response) {
    const { props } = this
    this.setState(
      updateCompleteReducer({
        updater,
        identifier,
        response,
        creator,
        props,
      })
    )

    // brand new items can now have any pending update requests applied
    if (creator && !update.updateAttempt) {
      this.applyDelayedUpdatesFor(identifier, getResponseId(response), item)
    }
  }

  updateError(updater, identifier, item, update, error) {
    const { updateAttempt } = update
    const newUpdate = {
      ...update,
      updateAttempt: updateAttempt + 1 || 1,
    }

    this.enqueueDelayedUpdate(item, newUpdate)

    if (newUpdate.updateAttempt < MAX_REQUEST_ATTEMPTS) {
      setTimeout(() => {
        this.applyDelayedUpdatesFor(identifier, null, item)
      }, 2000)
    }

    this.setState(updateErrorReducer(updater, error))
  }

  trackUpdate(updater, identifier, item, update, creator, setStateCallback) {
    this.setState(
      trackUpdateReducer({
        creator,
        identifier,
        item,
        props: this.props,
        update,
        updater,
      }),
      () => {
        if (setStateCallback && typeof setStateCallback === 'function') {
          setStateCallback()
        }
      }
    )

    if (updater && updater.then && typeof updater.then === 'function') {
      updater
        .then(
          this.updateComplete.bind(
            this,
            updater,
            identifier,
            creator,
            item,
            update
          )
        )
        .catch(this.updateError.bind(this, updater, identifier, item, update))
    }
  }

  addItem(position) {
    this.setState(newItemReducer(position, this.props), () => {
      if (this.props.onItemsChanged) {
        this.props.onItemsChanged(this.state.items)
      }
    })
  }

  removeItem(item) {
    const identifier = identifierFor(item)
    let updater = identifier
    if (item.id) {
      updater = this.props[methods.remove](item)
    } else {
      this.trackUpdate(updater, identifier, item, null, null, () => {
        if (this.props.onItemsChanged) {
          this.props.onItemsChanged(this.state.items)
        }
      })
    }
  }

  enqueueDelayedUpdate(item, update) {
    this.setState(enqueueDelayedUpdate(item, update))
  }

  applyDelayedUpdatesFor(identifier, responseId, newItem) {
    const { updateQueue = [] } = this.state
    const consolidatedUpdates = updateQueue
      .filter(([item]) => identifierFor(item) === identifier)
      .reduce((acc, [item, update]) => {
        const result = {
          ...item,
          ...(acc || {}),
          ...update,
        }

        if (responseId) {
          result.id = responseId
        }
        return result
      }, undefined)

    if (consolidatedUpdates) {
      this.updateItem({ ...newItem, id: responseId }, consolidatedUpdates)
    }
  }

  /**
   * Processes an update request for the specified item.
   * Note that this is not solely for updating existing items; creating a new
   * item also falls under the "update" category.
   */
  updateItem(item, update) {
    const itemId = identifierFor(item)
    // if this is an existing "real" item, send a call to update it
    if (item.id) {
      const updateItem = this.props[methods.update](item, update)
      return this.trackUpdate(updateItem, itemId, item, update)
    }

    // if this item is part of a pending "createItem" request,
    // enqueue the update request until the item is finished being created
    const { state } = this
    if (beingCreated(itemId, state) && !update.updateAttempt) {
      return this.enqueueDelayedUpdate(item, update)
    }

    // otherwise, create a new item
    const createItem = this.props[methods.create](item, update)
    return this.trackUpdate(createItem, itemId, item, update, createItem)
  }

  moveBy(item, amount) {
    const { updateItem } = this
    this.setState(
      moveByReducer({
        item,
        amount,
        ...this.props,
        updateItem: updateItem.bind(this, item),
      }),
      () => {
        if (this.props.onItemsChanged) {
          this.props.onItemsChanged(this.state.items)
        }
      }
    )
  }

  moveUp(item) {
    this.moveBy(item, -1)
  }

  moveDown(item) {
    this.moveBy(item, 1)
  }

  get otherProps() {
    /* eslint-disable no-unused-vars */
    const {
      addItemButton,
      addText,
      itemRenderer,
      items,
      placeholder,
      ...otherProps
    } = this.props
    /* eslint-enable no-unused-vars */

    return otherProps
  }

  itemProps(item, idx) {
    const { state } = this
    const { moveBy, moveDown, moveUp, removeItem, updateItem } = this

    return {
      ...this.otherProps,
      moveBy: moveBy.bind(this, item),
      moveDown: moveDown.bind(this, item),
      moveUp: moveUp.bind(this, item),
      remove: removeItem.bind(this, item),
      update: updateItem.bind(this, item),
      theme: this.props.theme,
      ...propsFor(item, idx, state),
    }
  }

  renderItem(item, idx) {
    const { itemRenderer: ItemRenderer } = this.props
    const { items } = this.state

    return (
      <ItemRenderer
        {...item}
        key={identifierFor(item)}
        className={kuiperClassNames('list-manager-item')}
        editing={this.props.editing}
        items={items}
        {...this.itemProps(item, idx)}
      />
    )
  }

  addButtonForPosition(position) {
    const { addItemButton: AddItemButton, theme } = this.props
    return (
      <AddItemButton
        onClick={this.addItem.bind(this, position)}
        key={`add-at-${position}`}
        {...{
          theme,
        }}
      />
    )
  }

  get itemsWithButton() {
    const { items } = this.state
    if (!items.length) {
      return [this.addButtonForPosition(0)]
    }
    const canAddMap = addMap(items)
    return items.reduce(
      (output, item, idx) => [
        ...output,
        this.renderItem(item, idx),
        ...(canAddMap[idx][1] && testFeature('workflow')
          ? [this.addButtonForPosition(idx + 1)]
          : []),
      ],
      canAddMap[0][0] && testFeature('workflow')
        ? [this.addButtonForPosition(0)]
        : []
    )
  }

  get items() {
    const { addItemButton } = this.props
    if (addItemButton) {
      return this.itemsWithButton
    }
    return this.state.items.map(this.renderItem.bind(this))
  }

  render() {
    return this.items
  }
}
